import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldCheck, X } from 'lucide-react';
import type { RoleExclusion, RoleTemplate, UserRoleAssignment } from '@cortex/domain';

import { cortexApi } from '@/services/cortexApi';
import { resolveApiError } from '@/services/errorMessages';
import { useToast } from '@/components/ui/Toast';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Skeleton } from '@/components/ui/shadcn/skeleton';

/**
 * Self-contained roles editor embedded in the user detail pane (PR #5a.4).
 *
 * Owns its own server state (the user's assignments + the assignable
 * templates) rather than threading it through UsersTab — role management
 * is a distinct concern with its own queries and mutations. Reaching the
 * Users tab already requires a user-management permission, and the API
 * re-enforces scope + the three assignment rules, so this component is
 * purely the convenient surface.
 *
 * The "add role" picker hides options that are already held or that would
 * trip a Segregation-of-Duties rule, so the common path never produces a
 * server rejection. The server remains the source of truth either way.
 */

export interface UserRolesPanelProps {
  readonly userId: string;
  /** The target user's entity type — only roles of this type are assignable. */
  readonly entityTypeSlug: string;
  readonly canManage: boolean;
}

const userRolesKey = (userId: string) => ['admin', 'user-roles', userId] as const;
const templatesKey = (entityTypeSlug: string) =>
  ['admin', 'role-templates', entityTypeSlug] as const;

export function UserRolesPanel({ userId, entityTypeSlug, canManage }: UserRolesPanelProps) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [pendingAdd, setPendingAdd] = useState<string>('');

  const rolesQuery = useQuery({
    queryKey: userRolesKey(userId),
    queryFn: () => cortexApi.roles.listForUser(userId),
  });

  const templatesQuery = useQuery({
    queryKey: templatesKey(entityTypeSlug),
    queryFn: () => cortexApi.roles.listTemplates({ entityTypeSlug }),
    enabled: canManage,
  });

  const assigned = useMemo<readonly UserRoleAssignment[]>(
    () => rolesQuery.data?.roles ?? [],
    [rolesQuery.data],
  );
  const templates = useMemo<readonly RoleTemplate[]>(
    () => templatesQuery.data?.roles ?? [],
    [templatesQuery.data],
  );
  const exclusions = useMemo<readonly RoleExclusion[]>(
    () => templatesQuery.data?.exclusions ?? [],
    [templatesQuery.data],
  );

  const assignedIds = useMemo(() => new Set(assigned.map((r) => r.roleTemplateId)), [assigned]);

  // Roles forbidden because they conflict (SoD) with something held.
  const conflictedIds = useMemo(() => {
    const blocked = new Set<string>();
    for (const pair of exclusions) {
      if (assignedIds.has(pair.roleATemplateId)) blocked.add(pair.roleBTemplateId);
      if (assignedIds.has(pair.roleBTemplateId)) blocked.add(pair.roleATemplateId);
    }
    return blocked;
  }, [exclusions, assignedIds]);

  const addableTemplates = useMemo(
    () => templates.filter((t) => !assignedIds.has(t.id) && !conflictedIds.has(t.id)),
    [templates, assignedIds, conflictedIds],
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: userRolesKey(userId) });

  const assignMutation = useMutation({
    mutationFn: (roleTemplateId: string) => cortexApi.roles.assign(userId, { roleTemplateId }),
    onSuccess: async () => {
      setPendingAdd('');
      showToast('Role assigned.', 'success');
      await invalidate();
    },
    onError: (err) => {
      const resolved = resolveApiError(err);
      showToast(`${resolved.title}: ${resolved.message}`, resolved.tone);
    },
  });

  const unassignMutation = useMutation({
    mutationFn: (roleTemplateId: string) => cortexApi.roles.unassign(userId, roleTemplateId),
    onSuccess: async () => {
      showToast('Role removed.', 'success');
      await invalidate();
    },
    onError: (err) => {
      const resolved = resolveApiError(err);
      showToast(`${resolved.title}: ${resolved.message}`, resolved.tone);
    },
  });

  const isMutating = assignMutation.isPending || unassignMutation.isPending;

  return (
    <section className="space-y-3" aria-label="Roles">
      <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Roles</h3>

      {rolesQuery.isLoading ? (
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-6 w-28 rounded-md" />
          <Skeleton className="h-6 w-24 rounded-md" />
        </div>
      ) : rolesQuery.isError ? (
        <p className="text-xs text-destructive" role="alert">
          Could not load this user&rsquo;s roles. Try reopening the record.
        </p>
      ) : assigned.length === 0 ? (
        <div className="flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
          No roles yet — this user can sign in but cannot do anything until a role is assigned.
        </div>
      ) : (
        <ul className="flex flex-wrap gap-2">
          {assigned.map((role) => (
            <li key={role.roleTemplateId}>
              <Badge variant="secondary" className="gap-1.5 py-1 pl-2.5 pr-1.5">
                <span>{role.displayName}</span>
                {canManage && (
                  <button
                    type="button"
                    aria-label={`Remove ${role.displayName}`}
                    title={`Remove ${role.displayName}`}
                    disabled={isMutating}
                    onClick={() => unassignMutation.mutate(role.roleTemplateId)}
                    className="inline-flex h-4 w-4 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-destructive/15 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                )}
              </Badge>
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <div className="flex items-center gap-2">
          <select
            aria-label="Add a role"
            className={SELECT_CLASS}
            value={pendingAdd}
            disabled={isMutating || templatesQuery.isLoading || addableTemplates.length === 0}
            onChange={(e) => setPendingAdd(e.target.value)}
          >
            <option value="">
              {templatesQuery.isLoading
                ? 'Loading roles…'
                : addableTemplates.length === 0
                  ? 'No more roles to add'
                  : 'Add a role…'}
            </option>
            {addableTemplates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.displayName}
              </option>
            ))}
          </select>
          <Button
            type="button"
            size="sm"
            disabled={!pendingAdd || isMutating}
            onClick={() => pendingAdd && assignMutation.mutate(pendingAdd)}
          >
            {assignMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              'Add'
            )}
          </Button>
        </div>
      )}
    </section>
  );
}

const SELECT_CLASS =
  'flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50';
